import { useQuery } from '@tanstack/react-query';
import { get } from '@/services/api-client';

/**
 * GET /api/Master/Relation?searchQuery=<q>&category=<cat>
 *
 * Mirrors Flutter `relationshipList()` →
 * `common_data_source_impl.dart:220-250`. Returns the master list of
 * relationship value-set entries used for emergency contact's
 * `relation` field. Each entry has Id / Code / Name / System.
 *
 * Important: the backend keys are PascalCase here (Id / Name etc.) but
 * the EmergencyContact save endpoint expects lowercase (`relation.name`,
 * `relationship` as the id). We normalise to lowercase fields the
 * caller actually consumes.
 */

export interface Relationship {
  id?: string;
  code?: string;
  name?: string;
  system?: string;
}

interface UseRelationshipsParams {
  query?: string;
  category?: string;
}

interface ApiRelationship {
  Id?: string;
  Code?: string;
  Name?: string;
  System?: string;
  // Some endpoints already lowercase the keys.
  id?: string;
  code?: string;
  name?: string;
  system?: string;
}

export function useRelationships(params: UseRelationshipsParams = {}) {
  return useQuery({
    queryKey: ['relationships', params.query ?? '', params.category ?? ''],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      qs.set('searchQuery', params.query ?? '');
      if (params.category) qs.set('category', params.category);
      const data = await get<ApiRelationship[]>(
        `/api/Master/Relation?${qs.toString()}`,
        { signal },
      );
      return (data ?? []).map<Relationship>((r) => ({
        id: r.Id ?? r.id,
        code: r.Code ?? r.code,
        name: r.Name ?? r.name,
        system: r.System ?? r.system,
      }));
    },
  });
}
